/**
 * Kernel Service - Interface to local Binetic AGI kernel
 */

import { authService } from "./authService";

const KERNEL_BASE = import.meta.env.VITE_KERNEL_URL || "http://127.0.0.1:3340";

// Helper function
async function readJsonOrText(r: Response): Promise<any> {
  const text = await r.text();
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, error: text };
  }
}

class KernelService {
  /**
   * Get kernel status
   */
  async getStatus(): Promise<any> {
    const r = await fetch(`${KERNEL_BASE}/status`);
    if (!r.ok) throw new Error(`Kernel status failed: ${r.status}`);
    return r.json();
  }

  /**
   * Generate content and write files
   */
  async generateAndWrite(task: string): Promise<number> {
    const session = authService.getSession();

    if (!session) {
      throw new Error("Authentication required");
    }

    // Use token if available from passkey auth, otherwise we might need password credentials
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (session.token) {
      headers["Authorization"] = `Bearer ${session.token}`;
    }

    const r = await fetch(`${KERNEL_BASE}/agent/generate-and-write`, {
      method: "POST",
      headers,
      body: JSON.stringify({ task }),
    });

    const json = await readJsonOrText(r);
    if (!r.ok || json.success !== true) {
      throw new Error(json.error ?? `Generate failed: ${r.status}`);
    }

    return typeof json.written === "number" ? json.written : 0;
  }

  /**
   * Check if kernel is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      await this.getStatus();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Generate content without writing
   */
  async generate(text: string): Promise<string> {
    const session = authService.getSession();

    if (!session) {
      throw new Error("Authentication required");
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (session.token) {
      headers["Authorization"] = `Bearer ${session.token}`;
    }

    const r = await fetch(`${KERNEL_BASE}/agent/generate`, {
      method: "POST",
      headers,
      body: JSON.stringify({ text }),
    });

    const json = await readJsonOrText(r);
    if (!r.ok || json.success !== true) {
      throw new Error(json.error ?? `Generate failed: ${r.status}`);
    }

    return json.generated || json.response || "";
  }

  /**
   * Execute a command/query on the system
   */
  async executeAction(action: string, params: Record<string, any> = {}): Promise<any> {
    const session = authService.getSession();

    if (!session) {
      throw new Error("Authentication required");
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (session.token) {
      headers["Authorization"] = `Bearer ${session.token}`;
    }

    const r = await fetch(`${KERNEL_BASE}/agent/execute`, {
      method: "POST",
      headers,
      body: JSON.stringify({ action, params }),
    });

    return readJsonOrText(r);
  }
}

// Export singleton instance
export const kernelService = new KernelService();
